/**
 * Phase 45 (2026-07-28) - Budget Settings page.
 *
 * Standalone page at /settings/budgets. Reuses BudgetSettingsModal in an
 * always-open embedded card style.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BudgetSettingsModal from "@/components/BudgetSettingsModal";
import api from "@/lib/api";

function currentMonthYYYYMM() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function BudgetSettingsPage() {
  const [open, setOpen] = useState(true);
  const [current, setCurrent] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(
        `/user/budget?kind=monthly_expenses&month=${currentMonthYYYYMM()}`,
      );
      setCurrent(data);
    } catch { /* noop */ }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="budget-settings-page">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1 text-sm hover:opacity-70"
        style={{ color: "var(--text-secondary)" }}
      >
        <ArrowLeft className="h-4 w-4" /> Back to Settings
      </Link>

      <div
        className="rounded-2xl border p-6"
        style={{
          backgroundColor: "var(--bg-surface)",
          borderColor: "var(--border-default)",
          boxShadow: "var(--shadow-card)",
        }}
      >
        <h2 className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>
          Monthly Expense Budget
        </h2>
        <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
          Configure the target amount used by the Total Expenses dashboard ring.
        </p>
        {current && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide"
                 style={{ color: "var(--text-muted)" }}>Current month</p>
              <p className="mt-1 tabular-nums font-medium"
                 style={{ color: "var(--text-primary)" }}>{current.month}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide"
                 style={{ color: "var(--text-muted)" }}>Saved amount</p>
              <p className="mt-1 tabular-nums font-medium"
                 style={{ color: "var(--text-primary)" }}>
                {current.amount_usd !== null && current.amount_usd !== undefined
                  ? `$${Number(current.amount_usd).toLocaleString("en-US", {
                        minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "Not set"}
              </p>
              {current.is_default && (
                <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  Auto-computed default
                </p>
              )}
            </div>
          </div>
        )}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-md px-4 py-1.5 text-sm font-medium"
            style={{
              backgroundColor: "var(--accent)",
              color: "var(--accent-fg, #FFFFFF)",
            }}
            data-testid="edit-budget-btn"
          >
            Edit budget
          </button>
        </div>
      </div>

      <BudgetSettingsModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
}
